import type { CampaignAnalytics, CampaignChannelRow, ChannelMetricDefinition } from '../../../api/client.ts';
import { PushMetricTooltip } from '../../analytics/push/MetricTooltip.tsx';
import { CHANNEL_LABELS } from '../labels.ts';
import { formatMetric } from './analytics.ts';

type NonPush = Exclude<CampaignChannelRow['channel'], 'push'>;
const ORDER: NonPush[] = ['content_card', 'in_app', 'email', 'sms', 'webhook'];
const definitionsOf = (data: CampaignAnalytics, channel: NonPush): ChannelMetricDefinition[] => (channel === 'content_card' ? data.channelMetrics.content_card : channel === 'in_app' ? data.channelMetrics.in_app : data.channelMetrics.other);

/**
 * Message Performance of the campaign's channels other than push (BB-104): Content Cards (created, impressions,
 * impression rate, clicks, CTR, dismissals), in-app (impressions, clicks, CTR, closes) and email / SMS / webhook
 * (sent, failed, failure rate). Each header opens the definition the API sends with the numbers; push has its own
 * per-platform table (BB-79).
 */
export function ChannelTable({ data }: { data: CampaignAnalytics }) {
  const rows = data.byChannel.filter((r): r is CampaignChannelRow & { channel: NonPush } => r.channel !== 'push');
  const channels = ORDER.filter((ch) => rows.some((r) => r.channel === ch));
  if (channels.length === 0) return null;
  return (
    <>
      {channels.map((channel) => {
        const defs = definitionsOf(data, channel);
        const label = CHANNEL_LABELS[channel];
        return (
          <div key={channel} className="push-table-scroll">
            <h3>{label}</h3>
            <table className="table push-table" aria-label={`${label} performance`}>
              <thead>
                <tr>
                  <th scope="col">Channel</th>
                  {defs.map((d) => (
                    <th key={d.key} scope="col">
                      <PushMetricTooltip definition={d}>
                        <span>{d.title.en}</span>
                      </PushMetricTooltip>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows
                  .filter((r) => r.channel === channel)
                  .map((r, i) => (
                    <tr key={`${r.platform ?? ''}${i}`}>
                      <th scope="row">
                        {label}
                        {r.platform ? ` · ${r.platform}` : ''}
                      </th>
                      {defs.map((d) => (
                        <td key={d.key}>{formatMetric(d.format, r.metrics[d.key])}</td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </>
  );
}
